import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  PhotoIcon,
  CalendarDaysIcon,
  ArrowUpOnSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/solid";
import toast, { Toaster } from "react-hot-toast";

import { rtdb } from "../../../config/firebase";
import {
  ref,
  push,
  remove,
  onValue,
  off,
} from "firebase/database";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

export default function KelolaProfil() {
  const [photos, setPhotos] = useState([]);
  const [newImageFile, setNewImageFile] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState(null);
  const [newImageDate, setNewImageDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const storage = getStorage();
  const BUCKET_PATH = "HalamanUtama/dokumentasi";

  // 🔹 Load galeri dari Firebase Realtime DB
  useEffect(() => {
    setIsLoading(true);
    const galleryRef = ref(rtdb, "halamanutama");

    const unsubscribe = onValue(
      galleryRef,
      (snapshot) => {
        const data = snapshot.val();
        const loadedPhotos = [];

        if (data) {
          for (const id in data) {
            loadedPhotos.push({
              id: id,
              ...data[id],
            });
          }
        }
        loadedPhotos.sort((a, b) => b.timestamp - a.timestamp);
        setPhotos(loadedPhotos);
        setIsLoading(false);
      },
      (error) => {
        console.error("❌ Gagal memuat data galeri:", error);
        toast.error("Gagal memuat galeri. Periksa koneksi Anda.");
        setIsLoading(false);
      }
    );

    return () => off(galleryRef, "value", unsubscribe);
  }, []);

  // 🔹 Preview file sebelum upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewImageFile(file);
      if (newImagePreview) URL.revokeObjectURL(newImagePreview);
      setNewImagePreview(URL.createObjectURL(file));
    }
  };

  // 🔹 Upload foto ke Firebase Storage
  const handleAddPhoto = async () => {
    if (!newImageFile || isUploading) return;

    setIsUploading(true);

    try {
      const fileName = `${Date.now()}_${newImageFile.name}`;
      const filePath = `${BUCKET_PATH}/${fileName}`;
      const storageReference = storageRef(storage, filePath);

      // Upload file
      await uploadBytes(storageReference, newImageFile);

      // Dapatkan URL publik
      const downloadURL = await getDownloadURL(storageReference);

      // Simpan metadata ke Firebase DB
      const photoData = {
        url: downloadURL,
        storagePath: filePath,
        date: newImageDate,
        timestamp: Date.now(),
      };

      await push(ref(rtdb, "halamanutama"), photoData);

      // Reset state
      setNewImageFile(null);
      if (newImagePreview) URL.revokeObjectURL(newImagePreview);
      setNewImagePreview(null);
      toast.success("Foto berhasil diunggah!");
    } catch (err) {
      console.error("Gagal upload:", err);
      toast.error(err.message || "Gagal menyimpan foto.");
    } finally {
      setIsUploading(false);
    }
  };

  // 🔹 Hapus foto dari Storage + DB
  const handleDeletePhoto = async (photo) => {
    if (
      !window.confirm(
        `Yakin ingin menghapus foto tanggal ${photo.date}?`
      )
    )
      return;

    try {
      // Hapus dari DB
      await remove(ref(rtdb, `halamanutama/${photo.id}`));

      // Hapus dari Storage (jika ada path)
      if (photo.storagePath) {
        const photoRef = storageRef(storage, photo.storagePath);
        await deleteObject(photoRef);
        toast.success("Foto berhasil dihapus dari server!");
      } else {
        toast.success("Data dihapus dari database!");
      }
    } catch (err) {
      console.error("Gagal hapus foto:", err);
      toast.error("Gagal menghapus foto dari server atau database.");
    }
  };

  // 🔹 Loading State
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <svg
          className="animate-spin h-8 w-8 text-green-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <p className="ml-3 text-gray-700">Memuat galeri...</p>
      </div>
    );
  }

  // 🔹 UI utama
  return (
    <div className="relative min-h-screen bg-slate-50">
      <Toaster position="top-right" reverseOrder={false} />

      <main className="relative z-10 p-4 md:p-6">
        <motion.header
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="sticky top-4 z-20 bg-white rounded-2xl shadow-lg border border-white/50 mb-8 p-4"
        >
          <h2 className="text-2xl font-bold text-gray-800">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-teal-500">
              Kelola
            </span>{" "}
            Dokumentasi Foto
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Tambah atau hapus foto untuk galeri dokumentasi.
          </p>
        </motion.header>

        {/* Upload Section */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.4 }}
        >
          <div className="p-6 rounded-2xl shadow-md border border-white/50 mb-8 grid grid-cols-1 md:grid-cols-3 gap-6 items-center bg-white">
            <div className="md:col-span-1">
              <label
                htmlFor="file-upload"
                className="cursor-pointer w-full aspect-square bg-transparent border-2 border-dashed rounded-lg flex flex-col items-center justify-center text-gray-400 hover:bg-gray-100 hover:border-green-500 transition"
              >
                {newImagePreview ? (
                  <img
                    src={newImagePreview}
                    alt="Preview"
                    className="w-full h-full object-cover rounded-lg"
                  />
                ) : (
                  <>
                    <PhotoIcon className="w-12 h-12" />
                    <span className="mt-2 text-sm font-semibold">
                      Pilih Gambar
                    </span>
                  </>
                )}
              </label>
              <input
                id="file-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>

            <div className="md:col-span-2 space-y-4">
              <div>
                <label
                  htmlFor="photo-date"
                  className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2"
                >
                  <CalendarDaysIcon className="w-5 h-5 text-gray-400" />
                  Tanggal Foto
                </label>
                <input
                  type="date"
                  id="photo-date"
                  value={newImageDate}
                  onChange={(e) => setNewImageDate(e.target.value)}
                  className="w-full max-w-xs border border-gray-300 rounded-md p-2 text-sm shadow-sm focus:ring-2 focus:ring-green-500 bg-white"
                />
              </div>
              <button
                onClick={handleAddPhoto}
                disabled={!newImageFile || isUploading}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-green-500 to-teal-600 text-white font-semibold rounded-lg shadow-md hover:from-green-600 hover:to-teal-700 transition-all duration-300 transform hover:scale-105 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed disabled:scale-100"
              >
                {isUploading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>Mengunggah...</span>
                  </>
                ) : (
                  <>
                    <ArrowUpOnSquareIcon className="w-5 h-5" />
                    <span>Upload Foto</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Galeri */}
          <div>
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              Galeri Tersimpan
            </h3>
            <AnimatePresence>
              {photos.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {photos.map((photo) => (
                    <motion.div
                      key={photo.id}
                      layout
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.5 }}
                      transition={{ duration: 0.3 }}
                      className="relative group aspect-square rounded-lg overflow-hidden shadow-md"
                    >
                      <a
                        href={photo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={photo.url}
                          alt={`Dokumentasi ${photo.date}`}
                          className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                        />
                      </a>

                      <div className="absolute bottom-0 left-0 p-3 text-white bg-black/30 w-full backdrop-blur-sm">
                        <p className="font-bold text-sm">
                          {new Date(photo.date).toLocaleDateString("id-ID", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })}
                        </p>
                      </div>

                      <button
                        onClick={() => handleDeletePhoto(photo)}
                        className="absolute top-2 right-2 p-1.5 bg-white/80 text-gray-800 rounded-full opacity-0 group-hover:opacity-100 hover:bg-red-600 hover:text-white transition-all"
                        title="Hapus Foto"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10 border-2 border-dashed rounded-lg text-gray-500 bg-transparent">
                  <p>Belum ada foto di galeri.</p>
                </div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
